/**
 * Background project-action runs, keyed by runId.
 *
 * Receives {@link ProjectScriptRunEvent} pushes from the server's
 * `workspace.projectScriptStatus` WS channel and exposes them to the UI (toast
 * + read-only output modal), the same way {@link useArchiveHookStore} does for
 * archive hooks. Keyed by run rather than by thread so two different actions
 * running on the same thread do not overwrite each other's output.
 */
import { create } from "zustand";

import type {
  ProjectScriptRunEvent,
  ProjectScriptRunResult,
  ThreadId,
} from "@taskin-code/contracts";

export interface ProjectScriptRun {
  readonly runId: string;
  readonly threadId: ThreadId;
  readonly scriptId: string;
  readonly scriptName: string;
  readonly command: string;
  readonly cwd: string;
  readonly startedAt: string;
  finishedAt: string | null;
  phase: ProjectScriptRunEvent["phase"];
  result: ProjectScriptRunResult | null;
  message: string | null;
}

interface ProjectScriptRunStore {
  readonly runs: Record<string, ProjectScriptRun>;
  readonly applyEvent: (event: ProjectScriptRunEvent) => void;
  readonly clear: (runId: string) => void;
}

export const useProjectScriptRunStore = create<ProjectScriptRunStore>((set) => ({
  runs: {},
  applyEvent: (event) =>
    set((state) => {
      const existing = state.runs[event.runId];
      const base: ProjectScriptRun = existing ?? {
        runId: event.runId,
        threadId: event.threadId,
        scriptId: event.scriptId,
        scriptName: event.scriptName,
        command: event.command,
        cwd: event.cwd,
        startedAt: event.startedAt,
        finishedAt: null,
        phase: event.phase,
        result: null,
        message: null,
      };

      const next: ProjectScriptRun = {
        ...base,
        phase: event.phase,
        result: event.result ?? base.result,
        message: event.message ?? base.message,
        finishedAt:
          event.phase === "completed" || event.phase === "error"
            ? event.occurredAt
            : base.finishedAt,
      };

      return { runs: { ...state.runs, [event.runId]: next } };
    }),
  clear: (runId) =>
    set((state) => {
      if (!(runId in state.runs)) return state;
      const next = { ...state.runs };
      delete next[runId];
      return { runs: next };
    }),
}));
